import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { cloneDeep, findIndex } from 'lodash';
import { Modalconfig, devicePixelRatio } from 'src/app/shared/constants';
import { Category, VBuilderTemplate, ContentNotification } from '../shared/models/v-builder.models';
import { VBuilderCategoryTemplateModalComponent } from './v-builder-category-template-modal/v-builder-category-template-modal.component';
import { VBuilderPlaybookModalComponent } from './v-builder-playbook-modal/v-builder-playbook-modal.component';
import { CoverAndColorsModalComponent } from '../../shared/components/cover-and-colors-modal/cover-and-colors-modal.component';
import { ContentNotificationsModalComponent } from '../../shared/components/content-notifications-modal/content-notifications-modal.component';
import { DuplicatePlaybookModalComponent } from '../../shared/components/duplicate-playbook-modal/duplicate-playbook-modal.component';
import { ConfirmationModelComponent } from 'src/app/shared/components/confirmation-model/confirmation-model.component';
import { CommonHttpService } from '../../shared/services/common-http/common-http.service';
import { SessionStorageService } from '../../shared/services/session-storage/session-storage.service';
import { sessionProfile } from '../../shared/interfaces/sessionStorageModel';
import { Subscription } from 'rxjs';
import { CommunicationService } from 'src/app/shared/services/communication/communication.service';
import { Playbook } from 'src/app/shared/model/playbook.models';

@Component({
    selector: 'app-v-builder-category-detail',
    templateUrl: './v-builder-category-detail.component.html',
    styleUrls: ['./v-builder-category-detail.component.scss'],
    standalone: false
})
export class VBuilderCategoryDetailComponent implements OnInit, OnDestroy {

  category: Category;
  categoryId: number;
  templateGroups = [];
  playbooks: Playbook[];
  duplicatedPlaybook: Playbook;
  modalRef: BsModalRef;
  categoryModalRef: BsModalRef;
  playbookModalRef: BsModalRef;
  notificationModalRef: BsModalRef;
  designModalRef: BsModalRef;
  confirmModalRef: BsModalRef;
  devicePixelRatio = devicePixelRatio;
  currentPlaybookId: number;
  isDelete = false;
  loggedInProfile: sessionProfile;
  contentNotifications: ContentNotification[];
  routeSubscription: Subscription;
  modalSubscription: Subscription;
  duplicatePbPlaceholderSub: Subscription;
  playbookSettingSubscription: Subscription;
  isPlaceholderPb = false;
  playbookDuplicated = false;
  currentPbId: any;
  previousElement: any;

  // To close dropmenu on click of any element other than playbook settings icon
  @HostListener('document:click') onClick() {
    this.removeDropmenuClass();
  }

  constructor(
    private commonHttpService: CommonHttpService,
    private modalService: BsModalService,
    private activatedRoute: ActivatedRoute,
    private sessionStorageService: SessionStorageService,
    private communicationService: CommunicationService
  ) { }

  ngOnInit() {
    this.loggedInProfile = this.sessionStorageService.getLoggedInProfileDetail();
    this.getCategory();
    this.getPageTemplateGroups();
    this.setPlaceholderPb();
    this.setChangedData();
    this.getAllPlaybooks();

    // to handle opacity of drafted playbooks on settings popup open
    $(document).on('show.bs.dropdown', () => {
      const element = document.getElementById('playbook-' + this.currentPbId);
      if (element) {
        this.previousElement = element;
        element.classList.add('pb-opacity');
      }
    });
    $(document).on('hide.bs.dropdown', () => {
      if (this.previousElement && this.previousElement.classList) {
        this.previousElement.classList.remove('pb-opacity');
      }
      const element = document.getElementById('playbook-' + this.currentPbId);
      if (element) {
        element.classList.remove('pb-opacity');
      }
    });
  }

  /**
   * To get the current category and to sort its templates by orderId
   */
  getCategory() {
    this.routeSubscription = this.activatedRoute.params.subscribe(data => {
      this.categoryId = parseInt(data.id, 10);
      if (this.categoryId) {
        this.commonHttpService.get<Category>({
          uri: `/api/VBuilder/GetCategoryById?id=${this.categoryId}`
        }).subscribe((response) => {
          this.category = response;
          if (this.category.vBuilderTemplates && this.category.vBuilderTemplates.length) {
            this.category.vBuilderTemplates.sort((tempA, tempB) => tempA.orderId - tempB.orderId);
          }
        });
      }
    });
  }

  /**
   * To fetch all the template groups
   */
  getPageTemplateGroups() {
    this.commonHttpService.get({
      uri: '/api/PageTemplates/GetPageTemplateGroupsWithPageTemplates?companyId=0',
    }
    ).subscribe((response: any) => {
      this.templateGroups = response;
    });
  }

  /**
   * To reflect changed data from category, playbook, or content notification modals
   */
  setChangedData() {
    this.playbookSettingSubscription = this.communicationService.getPlabookSettingSubject().subscribe((data) => {
      if (data) {
        if (data.modalType === 'vbCategoryModal') {
          this.handleCategory(data);
        } else if (data.modalType === 'notification') {
          this.handleNotification(data);
        } else if (data.modalType === 'design') {
          this.handleCoverAndColors(data);
        } else if (data.modalType === 'tutorialPlaybook') {
          this.handleTutorialPlaybook(data);
        } else if (data.modalType === 'duplicateTutorialPlaybook') {
          this.handlePbDuplication(data.value);
        }
        this.communicationService.setPlabookSettingSubject(null);
      }
    });
  }

  /**
   * To set placeholder playbook when it is duplicated
   * Sets a dummy playbook until duplication api completes
   */
  setPlaceholderPb() {
    this.duplicatePbPlaceholderSub = this.communicationService.getDuplicateTPbPlaceholder().subscribe(copies => {
      if (copies) {
        const index = findIndex(this.playbooks, (pb) => pb.id === this.duplicatedPlaybook.id);
        const placeholderPb = cloneDeep(this.duplicatedPlaybook);
        placeholderPb.coverAssetId = null;
        placeholderPb.isPlaceHolderPlaybook = true;
        if (copies === 1) {
          placeholderPb.title = this.duplicatedPlaybook.title + '-Copy';
        } else {
          placeholderPb.title = this.duplicatedPlaybook.title + '-Copy' + copies;
        }
        this.isPlaceholderPb = true;
        if (!this.playbookDuplicated) {
          this.playbooks.splice(index + 1, 0, placeholderPb);
        }
      }
    });
  }

  // below functions are the handlers for added or updated data
  /**
   * To handle added or updated template categories
   * @param data service object
   */
  handleCategory(data) {
    this.category.vBuilderTemplates = data.value;
    this.category.vBuilderTemplates.sort((tempA, tempB) => tempA.orderId - tempB.orderId);
  }

  /**
   * To handle added updated, or eleted content notification
   * @param data service object
   */
  handleNotification(data) {
    if (data.type === 'update') {
      const index = this.contentNotifications.findIndex(notification => notification.id === data.value.id);
      if (index >= 0) {
        this.contentNotifications[index] = data.value;
        this.setPlaybookNotification(data.value);
      }
    } else if (data.type === 'add') {
      this.contentNotifications.push(data.value);
      this.setPlaybookNotification(data.value);
    } else if (data.type === 'delete') {
      this.contentNotifications = this.contentNotifications.filter(notification => notification.id !== data.value);
      this.removePlaybookNotification(data.value);
    }
  }

  /**
   * To handle covers and colors data for playbook
   * @param designData service object
   */
  handleCoverAndColors(designData) {
    if (designData.value) {
      this.playbooks.forEach((playbook: Playbook) => {
        if (playbook.id === designData.value.id) {
          playbook.brandAssetId = designData.value.brandAssetId;
          playbook.coverAssetId = designData.value.coverAssetId;
          playbook.isDisplayPlaybookTitle = designData.value.isDisplayPlaybookTitle;
          playbook.coverHeaderBar = designData.value.coverHeaderBar;
          playbook.pageHeaderBar = designData.value.pageHeaderBar;
          playbook.sectionTitle = designData.value.sectionTitle;
          playbook.hyperlink = designData.value.hyperlink;
          playbook.activeSection = designData.value.activeSection;
          playbook.videoPlayButton = designData.value.videoPlayButton;
          playbook.sectionCallout = designData.value.sectionCallout;
          playbook.h4Headings = designData.value.h4Headings;
          playbook.underlineBlock = designData.value.underlineBlock;
          playbook.selectedTab = designData.value.selectedTab;
        }
      });
    }
  }

  /**
   * To handle added, updated or deleted playbooks
   * @param data service object
   */
  handleTutorialPlaybook(data) {
    if (data.type === 'add') {
      this.playbooks.push(data.value);
    } else if (data.type === 'update') {
      const index = this.playbooks.findIndex(playbook => playbook.id === data.value.id);
      if (index >= 0) {
        const notification = this.playbooks[index].contentNotification;
        this.playbooks[index] = data.value;
        this.playbooks[index].contentNotification = notification;
      }
    } else if (data.type === 'delete') {
      this.playbooks = this.playbooks.filter((playbook) => playbook.id !== data.value);
    }
  }

  /**
   * To handle duplication of playbook
   * @param playbook new duplicate playbook
   */
  handlePbDuplication(playbook: Playbook) {
    const index = findIndex(this.playbooks, (pb) => pb.id === this.duplicatedPlaybook.id);
    if (this.isPlaceholderPb) {
      this.playbooks[index + 1] = playbook;
    } else {
      this.playbooks.splice(index + 1, 0, playbook);
    }
    this.playbookDuplicated = true;
    this.isPlaceholderPb = false;
  }
  // handler functions end

  /**
   * To fetch all tutorial playbooks
   */
  getAllPlaybooks() {
    this.commonHttpService.get<Playbook[]>(
      {
        uri: '/api/VBuilder/GetAllTutorialPlaybooks'
      }).subscribe((response) => {
        this.playbooks = response;
        if (response.length) {
          this.getContentNotifications(response[0].companyId);
        }
      });
  }

  /**
   * To fetch content notifications for all the playbooks
   */
  getContentNotifications(companyId: number) {
    this.commonHttpService.get<ContentNotification[]>(
      {
        uri: `/api/ContentNotifications/GetAllContentNotification?companyId=${companyId}&profileId=${this.loggedInProfile.id}`
      })
      .subscribe((response) => {
        if (response.length) {
          this.contentNotifications = response;
          this.setContentNotifications(this.contentNotifications);
        }
      });
  }

  /**
   * To set added or updated notification in its respective playbook
   */
  setPlaybookNotification(notification: ContentNotification) {
    this.playbooks.forEach((playbook: Playbook) => {
      if (playbook.id === notification.playbookId) {
        playbook.contentNotification = notification;
      }
    });
  }

  /**
   * To remove deleted notification from playbook
   */
  removePlaybookNotification(deletedId: number) {
    this.playbooks.forEach((playbook: Playbook) => {
      if (playbook.contentNotification && playbook.contentNotification.id === deletedId) {
        playbook.contentNotification = null;
      }
    });
  }

  /**
   * To set content notifications for every playbook on initial page load
   */
  setContentNotifications(notifications: ContentNotification[]) {
    this.playbooks.forEach((playbook: Playbook) => {
      playbook.contentNotification = null;
      notifications.forEach((notification: ContentNotification) => {
        if (notification.playbookId === playbook.id) {
          playbook.contentNotification = notification;
        }
      });
    });
  }

  /**
   * Drop event of template drag & drop list
   */
  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.category.vBuilderTemplates, event.previousIndex, event.currentIndex);
    this.saveTemplateOrder();
  }

  /**
   * Category add/edit modal
   */
  openCategoryTempModal(template: VBuilderTemplate) {
    const initialState = {
      verb: template ? 'Edit' : 'Add',
      vBuilderTemplate: template ? cloneDeep(template) : new VBuilderTemplate(),
      templateGroups: this.templateGroups,
      categoryId: this.categoryId
    };
    this.categoryModalRef = this.modalService.show(VBuilderCategoryTemplateModalComponent,
      Object.assign({ initialState }, { class: 'custom-modal' }, Modalconfig));
  }

  /**
   * Playbook add/edit modal
   */
  openPlaybookModal(playbook: Playbook, verb: string) {
    this.removeDropmenuClass();
    const initialState = {
      verb,
      categoryId: this.categoryId,
      playbook: cloneDeep(playbook) || new Playbook(),
      order: this.playbooks ? this.playbooks.length : 0
    };
    this.playbookModalRef = this.modalService.show(VBuilderPlaybookModalComponent,
      Object.assign({ initialState }, { class: 'custom-modal' }, Modalconfig));
  }

  /**
   * Cover and colors modal
   */
  openCoverAndColorsModal(playbook: Playbook) {
    this.removeDropmenuClass();
    const initialState = {
      playbook: cloneDeep(playbook)
    };
    this.designModalRef = this.modalService.show(CoverAndColorsModalComponent,
      Object.assign({ initialState }, { class: 'custom-modal' }, Modalconfig));
  }

  /**
   * Content notifications modal
   */
  openContentNotificationsModal(playbook: Playbook) {
    this.removeDropmenuClass();
    const initialState = {
      verb: playbook.contentNotification ? 'Edit' : 'Add',
      notification: cloneDeep(playbook.contentNotification) || new ContentNotification(),
      playbookId: playbook.id,
      companyId: playbook.companyId
    };
    this.notificationModalRef = this.modalService.show(ContentNotificationsModalComponent,
      Object.assign({ initialState }, { class: 'custom-modal' }, Modalconfig));
  }

  /**
   * Duplicate playbook modal
   */
  openDuplicatePlaybookModal(playbook: Playbook) {
    this.removeDropmenuClass();
    this.duplicatedPlaybook = playbook;
    const initialState = {
      playbook: cloneDeep(playbook),
      isTutorialPlaybook: true
    };
    this.modalRef = this.modalService.show(DuplicatePlaybookModalComponent,
      Object.assign({ initialState }, { class: 'custom-modal' }, Modalconfig));
  }

  /**
   * To save the modified template listing order in drag & drop list
   */
  saveTemplateOrder() {
    const templateOrder = this.category.vBuilderTemplates.map((template, index) => {
      return {
        id: template.id,
        orderId: index
      };
    });
    if (templateOrder.length) {
      this.commonHttpService.post(
        {
          uri: '/api/VBuilder/ReorderTemplates',
          object: {
            vBuilderTemplates: templateOrder,
            vBuilderCatId: this.category.id
          },
          message: {
            successMessage: 'Page template successfully reordered.',
          }
        }).subscribe();
    }
  }

  /**
   * To delete template category
   */
  deleteTemplate(templateId: number) {
    const initialState = {
      msg: `Removing this template will remove associated action items from client vBuilder as well.
      Are you sure you want to remove this template?`
    };
    this.confirmModalRef = this.modalService.show(ConfirmationModelComponent,
      Object.assign({ initialState }, { class: 'custom-modal' }, Modalconfig));
    this.confirmModalRef.content.onclose.subscribe(isDelete => {
      if (isDelete) {
        this.commonHttpService.deleteWithBody(
          {
            uri: '/api/VBuilder/DeleteTemplate',
            object: {
              id: templateId
            },
            message: {
              successMessage: 'Template successfully removed from category.'
            }
          }
          , ).subscribe((response) => {
            this.isDelete = true;
            this.category.vBuilderTemplates = this.category.vBuilderTemplates.filter(template => template.id !== templateId);
          });
      }
      this.confirmModalRef.hide();
    });
  }

  goToPlaybook(playbookId) {
    window.location.href = `/playbook/${playbookId}?languageId=1&r=vbuilder/category/detail/${this.categoryId}`;
  }

  removeDropmenuClass() {
    const element = $(`#dropMenu${this.currentPlaybookId}`);
    if (element) {
      element.removeClass('open');
    }
  }

  dropDownClick(playbookId: number) {
    // to remove 'open' class for previous playbook's drop menu
    this.removeDropmenuClass();
    this.currentPlaybookId = playbookId;
    $(`#dropMenu${playbookId}`).addClass('dropdown open');
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }
    if (this.playbookSettingSubscription) {
      this.playbookSettingSubscription.unsubscribe();
    }
    if (this.duplicatePbPlaceholderSub) {
      this.duplicatePbPlaceholderSub.unsubscribe();
    }
  }
}
